import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { IoIosArrowDown } from "react-icons/io";
import { useQuery } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { getMovie } from "../api/movies";
import { searchMoviesByKeyword, searchTvShowsByKeyword } from "../api/search";
import { getTvShowDetails } from "../api/tvShows";
import MovieModal from "../Components/MovieModal";
import TvModal from "../Components/TvModal";
import { makeImagePath } from "../utils";

const SearchContainer = styled.div`
  margin: 100px;
`;

const SearchHeader = styled.div`
  font-size: 1.5vw;
  color: #808080;
  padding-bottom: 70px;
  span {
    font-weight: 600;
    font-size: 1.5vw;
    color: white;
  }
`;

const SearchResults = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
`;

const BoxImg = styled.img`
  width: 100%;
  height: 8.5vw;
  display: block; // to remove the white space under the image
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
`;

const NoBoxImg = styled.div`
  width: 100%;
  height: 8.5vw;
  /* display: block; // to remove the white space under the image */
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: black;
  h4 {
    font-size: 0.7vw;
  }
  div {
    font-size: 0.6vw;
  }
`;

const InfoBtn = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  margin: 10px;
  display: none;
  border-radius: 50%;
  width: 1.7vw;
  height: 1.7vw;
  background-color: #2a2a2a;
  border: 2px solid white;
`;

const Box = styled(motion.div)`
  background-color: transparent;
  font-size: 66px;
  border-radius: 5px;
  display: flex;
  margin: 5px;
  flex-direction: column;
  &:first-child {
    div {
      transform-origin: center left;
    }
  }
  &:last-child {
    div {
      transform-origin: center right;
    }
  }
  &:hover ${BoxImg} {
    transition-delay: 0.5s;
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
  }
`;

const InfoContainer = styled(motion.div)`
  border-radius: 5px;
  &:hover {
    position: relative;
  }
  &:hover ${InfoBtn} {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
`;

const Info = styled(motion.div)`
  padding: 15px 10px 10px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  background-color: #181818;
  opacity: 0;
  width: 100%;
  bottom: 0;
  h4 {
    text-align: left;
    font-size: 0.8vw;
    margin-bottom: 0.5vw;
  }
  h5 {
    margin-bottom: 0.5vw;
  }
`;

const SearchCategory = styled.div`
  border: 1px solid white;
  border-radius: 3px;
  padding: 0 5px;
  margin-right: 10px;
  color: white;
  font-weight: normal;
`;

const InfoMatchRating = styled.h5`
  display: flex;
  align-items: center;
  text-align: left;
  font-size: 0.6vw;
  font-weight: bold;
  color: #47d369;
  div {
    margin-right: 10px;
  }
`;

const boxVariants = {
  normal: {
    scale: 1,
  },
  hover: {
    scale: 1.3,
    y: -80,
    boxShadow: "0px 0px 15px 2px black",
    backgroundColor: "#181818",
    transition: {
      delay: 0.5,
      type: "tween",
    },
  },
};

const infoVariants = {
  hover: {
    opacity: 1,
    transition: {
      delay: 0.7,
      type: "tween",
    },
  },
};

function Search() {
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const keyword = params.get("keyword");
  const category = params.get("category");
  const mediaId = params.get("id");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMovieId, setSelectedMovieId] = useState<any>(null);
  const [selectedTvShowId, setSelectedTvShowId] = useState<any>(null);
  const searchMovieMatch =
    category === "movie" && Number(mediaId) === Number(selectedMovieId);
  const searchTvMatch =
    category === "tv" && Number(mediaId) === Number(selectedTvShowId);
  const { data: movieData, isLoading: movieIsLoading } = useQuery({
    queryKey: ["searchResults", "movies", keyword],
    queryFn: () => {
      const result = searchMoviesByKeyword(keyword ? keyword : "");
      return result;
    },
    enabled: !!keyword,
  });
  const { data: tvShowData, isLoading: tvShowsIsLoading } = useQuery({
    queryKey: ["searchResults", "tvShows", keyword],
    queryFn: () => {
      const result = searchTvShowsByKeyword(keyword ? keyword : "");
      return result;
    },
    enabled: !!keyword,
  });
  const { data: searchMovieData, isLoading: searchMovieIsLoading } = useQuery({
    queryKey: ["movie", selectedMovieId],
    queryFn: () => {
      return getMovie(Number(selectedMovieId));
    },
    enabled: Boolean(isModalOpen && selectedMovieId),
  });
  const { data: searchTvData, isLoading: searchTvIsLoading } = useQuery({
    queryKey: ["tvShow", selectedTvShowId],
    queryFn: () => {
      return getTvShowDetails(Number(selectedTvShowId));
    },
    enabled: Boolean(isModalOpen && selectedTvShowId),
  });
  const isLoading = movieIsLoading && tvShowsIsLoading;
  const onOverlayClick = () => {
    if (selectedMovieId) setSelectedMovieId(null);
    if (selectedTvShowId) setSelectedTvShowId(null);
    setIsModalOpen(false);
    history.push(`/search?keyword=${keyword}`);
  };
  const onBoxClicked = (category: string, mediaId: number) => {
    if (category === "movie") {
      setSelectedMovieId(mediaId);
      history.push(`search?keyword=${keyword}&category=movie&id=${mediaId}`);
    } else {
      setSelectedTvShowId(mediaId);
      history.push(`search?keyword=${keyword}&category=tv&id=${mediaId}`);
    }
  };

  useEffect(() => {
    if (searchTvMatch) setIsModalOpen(true);
  }, [searchTvMatch]);

  useEffect(() => {
    if (searchMovieMatch) setIsModalOpen(true);
  }, [searchMovieMatch]);
  return (
    <>
      <SearchContainer>
        <SearchHeader>
          Search Results related to: <span>{keyword}</span>
        </SearchHeader>
        {!isLoading ? (
          <>
            <SearchResults>
              {movieData?.results.map((movie) => (
                <Box key={`search_${movie.id}`} layoutId={`search_${movie.id}`}>
                  <InfoContainer
                    whileHover="hover"
                    initial="normal"
                    variants={boxVariants}
                    transition={{ type: "tween" }}
                    onClick={() => {
                      onBoxClicked("movie", movie.id);
                    }}
                  >
                    {movie.backdrop_path ? (
                      <BoxImg
                        src={makeImagePath(movie.backdrop_path, "w500")}
                      />
                    ) : (
                      <NoBoxImg>
                        <h4>
                          Title:{" "}
                          <span style={{ fontWeight: 600 }}>{movie.title}</span>
                        </h4>
                        <div>No image available.</div>
                      </NoBoxImg>
                    )}
                    <InfoBtn>
                      <IoIosArrowDown color="white" />
                    </InfoBtn>
                    <Info variants={infoVariants}>
                      <h4>{movie.title}</h4>
                      <InfoMatchRating>
                        <SearchCategory>Movie</SearchCategory>
                        <div style={{ color: "white" }}>
                          {movie.release_date
                            ? movie.release_date.split("-")[0]
                            : "N/A"}
                        </div>
                        <div>{movie.vote_average * 10}% Match</div>
                      </InfoMatchRating>
                    </Info>
                  </InfoContainer>
                </Box>
              ))}
              {tvShowData?.results.map((tvShow) => (
                <Box
                  key={`search_${tvShow.id}`}
                  layoutId={`search_${tvShow.id}`}
                >
                  <InfoContainer
                    whileHover="hover"
                    initial="normal"
                    variants={boxVariants}
                    transition={{ type: "tween" }}
                    onClick={() => {
                      onBoxClicked("tv", tvShow.id);
                    }}
                  >
                    {tvShow.backdrop_path ? (
                      <BoxImg
                        src={makeImagePath(tvShow.backdrop_path, "w500")}
                      />
                    ) : (
                      <NoBoxImg>
                        <h4>
                          Name:{" "}
                          <span style={{ fontWeight: 600 }}>{tvShow.name}</span>
                        </h4>
                        <div>No image available.</div>
                      </NoBoxImg>
                    )}
                    <InfoBtn>
                      <IoIosArrowDown color="white" size={20} />
                    </InfoBtn>
                    <Info variants={infoVariants}>
                      <h4>{tvShow.name}</h4>
                      <InfoMatchRating>
                        <SearchCategory>TV Show</SearchCategory>
                        <div style={{ color: "white" }}>
                          {tvShow.first_air_date
                            ? tvShow.first_air_date.split("-")[0]
                            : "N/A"}
                        </div>
                        <div>{tvShow.vote_average * 10}% Match</div>
                      </InfoMatchRating>
                    </Info>
                  </InfoContainer>
                </Box>
              ))}
            </SearchResults>
          </>
        ) : null}
      </SearchContainer>
      <MovieModal
        movieMatch={searchMovieMatch}
        onOverlayClick={onOverlayClick}
        selectedMovieId={selectedMovieId}
        data={searchMovieData}
        isLoading={searchMovieIsLoading}
      />
      <TvModal
        tvMatch={searchTvMatch}
        onOverlayClick={onOverlayClick}
        selectedTvShowId={selectedTvShowId}
        data={searchTvData}
        isLoading={searchTvIsLoading}
      />
    </>
  );
}
export default Search;
